import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useLazyGetUserHistoryFilterDataQuery,
  useLazyGetUserHistorySearchResultQuery,
  useSearchUserHistoryMutation,
} from '@app/api/Users';
import {
  // AI_OPERATOR_LABEL,
  DATE_FORMATS,
  MOBILE,
  RIGHTS_HISTORY,
  RIGHTS_IS_ON,
  today,
} from '@app/constants';
import {
  useAccessRights,
  useAccessRightsToAi,
  useContentWidth,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NActionsHistory, NSmallGlobalEntities } from '@app/types';

import {
  FIRST_BREAKPOINT,
  THIRD_BREAKPOINT,
  FOURTH_BREAKPOINT,
  SECOND_BREAKPOINT,
} from '@pages/Users/ActionsHistoryPage/data/breakpoints';
import { defaultSearchFilters } from '@pages/Users/ActionsHistoryPage/data/defaultSearchFilters';
import { getDayStartTimestamp } from '@pages/Users/ActionsHistoryPage/utils/getDayStartTimestamp';

import type { PaginationProps } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { BaseOptionType } from 'antd/es/select';

const { ItemsPerPage } = defaultSearchFilters;

export const useActionsHistory = () => {
  const [searchParams] = useSearchParams();
  const isAiOperatorHistoryOnly = searchParams.get('user') === 'ai';
  const [aiOperatorId] = useState(0);

  const { activeBranchId, database, userId } = useUserPrint();
  const { showNotification } = useNotification();
  const [getUserHistoryFilterData] = useLazyGetUserHistoryFilterDataQuery();
  const [getUserHistorySearchResult] = useLazyGetUserHistorySearchResultQuery();
  const [searchUserHistory] = useSearchUserHistoryMutation();
  const [logs, setLogs] = useState<NActionsHistory.Action[]>([]);
  const [mainContentLoader, setMainContentLoader] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(() => ItemsPerPage);
  const [page, setPage] = useState(1);
  const [filters, setFilters] =
    useState<NActionsHistory.UserHistoryFilterData | null>(null);
  const [filtersLoader, setFiltersLoader] = useState(false);
  const [selectedActions, setSelectedActions] = useState<
    BaseOptionType[] | null
  >(null);
  const [selectedUsers, setSelectedUsers] = useState<BaseOptionType[] | null>(
    null
  );
  const [searchButtonLoader, setSearchButtonLoader] = useState(false);
  const [resetButtonLoader, setResetButtonLoader] = useState(false);
  const [isImportant, setImportant] = useState(false);
  const [datesRange, setDatesRange] = useState<NSmallGlobalEntities.RangeValue>(
    [dayjs(getDayStartTimestamp(today)), today]
  );
  const { contentWidth } = useContentWidth();
  const isThirdBreakpoint = contentWidth > THIRD_BREAKPOINT;
  const isSecondBreakpoint = contentWidth > SECOND_BREAKPOINT;
  const isFirstBreakpoint = contentWidth > FIRST_BREAKPOINT;

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    const yearHalf = dayjs(
      new Date(Date.now() - (366 / 2) * 24 * 60 * 60 * 1000)
    );

    return current && current < yearHalf.endOf('day');
  };

  const [logsTotal, setLogsTotal] = useState<number | null>(null);
  const [searchId, setSearchId] = useState<number | null>(null);
  const [areFiltersOpened, setFiltersOpening] = useState<boolean>(
    contentWidth > FOURTH_BREAKPOINT
  );

  const { innerWidth } = useWindowSize();
  const isMobile = innerWidth <= MOBILE;

  const { hasUserRight: hasUserRightForModuleView } = useAccessRights(
    RIGHTS_HISTORY,
    RIGHTS_IS_ON
  );
  const { hasOrganizationRightToAi, hasUserSpecialRightToAi } =
    useAccessRightsToAi();

  const fetchUserHistoryFilterData = useCallback(async () => {
    if (!activeBranchId || !database || !userId) return;
    setFiltersLoader(true);
    try {
      const allFilters = await getUserHistoryFilterData({
        ActiveBranchId: activeBranchId,
        logs: { isLogged: true, userId, database },
      }).unwrap();

      const areMinimalRightsToAi =
        hasOrganizationRightToAi && hasUserSpecialRightToAi;
      const filtersWithoutAiOperator = {
        ...allFilters,
        Users: allFilters.Users.filter(({ Id }) => Id !== aiOperatorId),
      };
      const filtersCheckedForAccessToAi = areMinimalRightsToAi
        ? allFilters
        : filtersWithoutAiOperator;

      setFilters(filtersCheckedForAccessToAi);
    } catch (error) {
      showNotification(error);
    } finally {
      setFiltersLoader(false);
    }
  }, [
    activeBranchId,
    database,
    userId,
    getUserHistoryFilterData,
    hasOrganizationRightToAi,
    hasUserSpecialRightToAi,
    showNotification,
    aiOperatorId,
  ]);

  const requestUserIds = useMemo(() => {
    const userIds = isAiOperatorHistoryOnly
      ? [aiOperatorId].filter((id) => id !== undefined && id !== null)
      : defaultSearchFilters.UserIds;
    return userIds;
  }, [aiOperatorId, isAiOperatorHistoryOnly]);

  const normalizeUserIds = useCallback(
    (options: BaseOptionType[] | null | undefined): number[] => {
      const numeric = (options ?? [])
        .filter((o) => o?.value !== undefined && o?.value !== null)
        .map((o) => Number(o?.value))
        .filter((n) => Number.isFinite(n)) as number[];

      return numeric.length > 0 ? numeric : requestUserIds ?? [];
    },
    [requestUserIds]
  );

  const setLogsWithCount = useCallback(
    ({
      UserChanges,
      CountUserChanges,
    }: {
      UserChanges: NActionsHistory.Action[] | null;
      CountUserChanges: number;
    }): void => {
      const handledUserChanges = UserChanges ?? [];
      const aiOperatorHistoryOnly = handledUserChanges.filter(
        ({ ActionUserId }) => ActionUserId === aiOperatorId
      );
      const historyWithoutAiActions = handledUserChanges.filter(
        ({ ActionUserId }) => ActionUserId !== aiOperatorId
      );
      const areRightsToAi = hasOrganizationRightToAi && hasUserSpecialRightToAi;

      const setLogsWithCountForAiMode = (
        areRightsToAi: boolean,
        aiOperatorHistoryOnly: NActionsHistory.Action[],
        CountUserChanges: number
      ) => {
        if (areRightsToAi) {
          setLogs(aiOperatorHistoryOnly);
          return setLogsTotal(CountUserChanges);
        }

        setLogs([]);
        return setLogsTotal(0);
      };

      if (isAiOperatorHistoryOnly) {
        setLogsWithCountForAiMode(
          areRightsToAi,
          aiOperatorHistoryOnly,
          CountUserChanges
        );
      }

      if (areRightsToAi) {
        setLogs(handledUserChanges);
        return setLogsTotal(CountUserChanges);
      }

      setLogs(historyWithoutAiActions);
      setLogsTotal(CountUserChanges - 1);
    },
    [
      aiOperatorId,
      hasOrganizationRightToAi,
      hasUserSpecialRightToAi,
      isAiOperatorHistoryOnly,
    ]
  );

  const fetchUserHistory = useCallback(async () => {
    if (isAiOperatorHistoryOnly && !aiOperatorId) return;

    setMainContentLoader(true);
    try {
      const { UserChanges, CountUserChanges, Id } = await searchUserHistory({
        ActiveBranchId: activeBranchId,
        data: {
          ...defaultSearchFilters,
          UserIds: requestUserIds,
          ItemsPerPage: rowsPerPage,
        },
      }).unwrap();

      setLogsWithCount({ UserChanges, CountUserChanges });
      setSearchId(Id);
    } catch (error) {
      showNotification(error);
    } finally {
      setMainContentLoader(false);
    }
  }, [
    isAiOperatorHistoryOnly,
    aiOperatorId,
    activeBranchId,
    requestUserIds,
    rowsPerPage,
    setLogsWithCount,
    showNotification,
    searchUserHistory,
  ]);

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = useCallback(
    async (_: number, pageSize: number) => {
      setRowsPerPage(pageSize);

      if (searchId) {
        try {
          const DateStart = dayjs(datesRange?.[0]).format(
            DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
          );
          const DateEnd = dayjs(datesRange?.[1]).format(
            DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
          );

          const UserIds = normalizeUserIds(selectedUsers);

          const Keys =
            selectedActions && selectedActions.length > 0
              ? selectedActions.map(({ value }) => String(value))
              : null;

          const requestData = {
            ...defaultSearchFilters,
            DateStart,
            DateEnd,
            UserIds,
            Keys,
            IsOnlyImportant: isImportant,
            ItemsPerPage: pageSize,
          };

          const { UserChanges, CountUserChanges } = await searchUserHistory({
            ActiveBranchId: activeBranchId,
            data: requestData,
          }).unwrap();

          setLogsWithCount({ UserChanges, CountUserChanges });
        } catch (error) {
          showNotification(error);
        }
      }
    },
    [
      activeBranchId,
      searchId,
      setLogsWithCount,
      showNotification,
      datesRange,
      isImportant,
      selectedActions,
      selectedUsers,
      normalizeUserIds,
      searchUserHistory,
    ]
  );

  const handleNames = useCallback(
    (lastName: string | null, firstName: string | null) => {
      const names = [lastName, firstName].filter(Boolean);
      return names.length > 0 ? names.join(' ') : '-';
    },
    []
  );

  const runSearch = useCallback(
    async (
      selectedUsers: BaseOptionType[] | null,
      selectedActions: BaseOptionType[] | null,
      isImportant: boolean,
      datesRange: NSmallGlobalEntities.RangeValue
    ) => {
      setSearchButtonLoader(true);
      setMainContentLoader(true);

      try {
        const DateStart = dayjs(datesRange?.[0]).format(
          DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE
        );
        const DateEnd = dayjs(datesRange?.[1])
          .endOf('day')
          .format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE);

        const UserIds = normalizeUserIds(selectedUsers);

        const Keys =
          selectedActions && selectedActions.length > 0
            ? selectedActions.map(({ value }) => String(value))
            : null;

        const requestData = {
          ...defaultSearchFilters,
          DateStart,
          DateEnd,
          UserIds,
          Keys,
          IsOnlyImportant: isImportant,
          ItemsPerPage: rowsPerPage,
        };

        const {
          UserChanges = [],
          CountUserChanges,
          Id,
        } = await searchUserHistory({
          ActiveBranchId: activeBranchId,
          data: requestData,
        }).unwrap();

        setLogsWithCount({ UserChanges, CountUserChanges });
        setSearchId(Id);
      } catch (error) {
        showNotification(error);
      } finally {
        setSearchButtonLoader(false);
        setMainContentLoader(false);
      }
    },
    [
      activeBranchId,
      setLogsWithCount,
      showNotification,
      normalizeUserIds,
      rowsPerPage,
      searchUserHistory,
    ]
  );

  const prepareAllActions = useCallback(
    (filters: NActionsHistory.UserHistoryFilterData | null) =>
      Object.entries(filters?.Keys ?? {}).map(([key, val]) => ({
        value: key,
        label: val,
      })),
    []
  );

  const clearFilters = useCallback(async () => {
    setDatesRange([today, today]);
    setSelectedUsers([]);
    setSelectedActions([]);
    setImportant(false);
    setResetButtonLoader(true);

    try {
      const { UserChanges, CountUserChanges, Id } = await searchUserHistory({
        ActiveBranchId: activeBranchId,
        data: {
          ...defaultSearchFilters,
          UserIds: requestUserIds,
        },
      }).unwrap();

      setLogs(UserChanges ?? []);
      setLogsTotal(CountUserChanges);
      setSearchId(Id);
      setPage(1);
      setRowsPerPage(10);
    } catch (error) {
      showNotification(error);
    } finally {
      setResetButtonLoader(false);
    }
  }, [activeBranchId, requestUserIds, showNotification, searchUserHistory]);

  const changePage = useCallback(
    async (newPage: number, pageSize: number) => {
      if (!searchId) return;
      setRowsPerPage(pageSize);
      setPage(newPage);

      setMainContentLoader(true);
      setPage(newPage);

      try {
        const { UserChanges, CountUserChanges, Id } =
          await getUserHistorySearchResult({
            ActiveBranchId: activeBranchId,
            searchId,
            page: newPage,
            itemsPerPage: pageSize,
          }).unwrap();

        setLogs(UserChanges ?? []);
        setLogsTotal(CountUserChanges);
        setSearchId(Id);
      } catch (error) {
        showNotification(error);
      } finally {
        setMainContentLoader(false);
      }
    },
    [searchId, activeBranchId, showNotification, getUserHistorySearchResult]
  );

  const getButtonWidth = useCallback(() => {
    if (isFirstBreakpoint) return 'auto';

    if (isThirdBreakpoint && contentWidth < FIRST_BREAKPOINT) return '180px';

    return '100%';
  }, [contentWidth, isFirstBreakpoint, isThirdBreakpoint]);

  const isMobileFilters = useMemo(
    () => contentWidth <= FOURTH_BREAKPOINT,
    [contentWidth]
  );

  const toggleFilters = useCallback(() => {
    setFiltersOpening((prev) => !prev);
  }, []);

  const actionsWithHandledLabels = useMemo(
    () =>
      filters
        ? prepareAllActions(filters).map(({ value, label }) => {
            switch (value) {
              case 'ChangeClientServiceExcelImport':
                return {
                  value,
                  label: 'Изменение при импорте услуг из Excel',
                };
              default:
                return {
                  value,
                  label,
                };
            }
          })
        : [],
    [filters, prepareAllActions]
  );

  const show403Unauthorized = useMemo(() => {
    return (
      (isAiOperatorHistoryOnly && !hasOrganizationRightToAi) ||
      (isAiOperatorHistoryOnly && !hasUserSpecialRightToAi) ||
      !hasUserRightForModuleView
    );
  }, [
    isAiOperatorHistoryOnly,
    hasOrganizationRightToAi,
    hasUserSpecialRightToAi,
    hasUserRightForModuleView,
  ]);

  const forbidden403PageTitle = useMemo(
    () =>
      isAiOperatorHistoryOnly && !hasOrganizationRightToAi
        ? 'У вашей организации нет прав доступа к этой части системы.'
        : '',
    [isAiOperatorHistoryOnly, hasOrganizationRightToAi]
  );

  const showFilters = useMemo(() => {
    if (isMobileFilters) {
      return areFiltersOpened;
    }
    return true;
  }, [areFiltersOpened, isMobileFilters]);
  return {
    fetchUserHistoryFilterData,
    logs,
    mainContentLoader,
    rowsPerPage,
    page,
    filters,
    filtersLoader,
    selectedActions,
    setSelectedActions,
    selectedUsers,
    setSelectedUsers,
    searchButtonLoader,
    resetButtonLoader,
    isImportant,
    setImportant,
    datesRange,
    setDatesRange,
    disabledDate,
    logsTotal,
    isMobile,
    fetchUserHistory,
    onShowSizeChange,
    handleNames,
    runSearch,
    clearFilters,
    changePage,
    getButtonWidth,
    isThirdBreakpoint,
    isSecondBreakpoint,
    isFirstBreakpoint,
    isMobileFilters,
    areFiltersOpened,
    toggleFilters,
    actionsWithHandledLabels,
    show403Unauthorized,
    forbidden403PageTitle,
    showFilters,
  };
};
